<template>
  <AppShell
    active="favorites"
    :workspace="workspace"
    title="Favorite opportunities"
    subtitle="Pinned acquisition work that needs fast access and frequent review."
    max-width-class="max-w-none"
    data-testid="favorite-opportunities-page"
  >
    <template #actions>
      <a href="/map" class="hidden h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted sm:inline-flex">
        <MapPinned class="size-4" aria-hidden="true" />
        Map
      </a>
      <a href="/opportunities" class="inline-flex h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover">
        <CircleDollarSign class="size-4" aria-hidden="true" />
        Pipeline
      </a>
    </template>

    <div v-if="notice" class="mb-4 rounded-md border border-land-border bg-land-primary-soft px-4 py-3 text-sm font-medium text-land-primary" role="status">{{ notice }}</div>
    <div v-if="errors.length > 0" class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
      <p v-for="error in errors" :key="error">{{ error }}</p>
    </div>

    <section class="grid gap-3 md:grid-cols-3" aria-label="Favorite opportunity summary">
      <article class="rounded-md border border-land-border bg-land-surface p-4 shadow-sm">
        <p class="text-sm font-medium text-land-ink-muted">Favorites</p>
        <p class="mt-2 text-2xl font-semibold">{{ formatNumber(favoriteCount) }}</p>
        <p class="mt-3 text-xs text-land-ink-soft">Pinned opportunities in this workspace</p>
      </article>
      <article class="rounded-md border border-land-border bg-land-surface p-4 shadow-sm">
        <p class="text-sm font-medium text-land-ink-muted">Needs attention</p>
        <p class="mt-2 text-2xl font-semibold">{{ formatNumber(attentionCount) }}</p>
        <p class="mt-3 text-xs text-land-ink-soft">Blocked, overdue, stale, or incomplete</p>
      </article>
      <article class="rounded-md border border-land-border bg-land-surface p-4 shadow-sm">
        <p class="text-sm font-medium text-land-ink-muted">Avg. diligence</p>
        <p class="mt-2 text-2xl font-semibold">{{ formatNumber(averageDiligence) }}%</p>
        <p class="mt-3 text-xs text-land-ink-soft">Progress across favorited sites</p>
      </article>
    </section>

    <section v-if="opportunities.length > 0" class="mt-4 rounded-md border border-land-border bg-land-surface shadow-sm">
      <div class="flex items-center justify-between gap-3 border-b border-land-border-soft p-4">
        <div>
          <h2 class="text-lg font-semibold tracking-normal">Pinned deal list</h2>
          <p class="mt-1 text-sm text-land-ink-muted">{{ formatNumber(opportunities.length) }} active favorites</p>
        </div>
      </div>

      <div class="divide-y divide-land-border-soft">
        <article
          v-for="opportunity in opportunities"
          :key="opportunity.id"
          class="grid gap-4 border-l-4 px-4 py-4 lg:grid-cols-[minmax(0,1.35fr)_180px_240px_180px_110px] lg:items-center"
          :class="rowStateClasses(opportunity)"
          :data-testid="`favorite-row-${opportunity.id}`"
        >
          <div class="flex min-w-0 items-start gap-3">
            <button
              type="button"
              class="grid size-9 shrink-0 place-items-center rounded-md border border-amber-200 bg-amber-50 text-amber-500 hover:bg-amber-100 disabled:cursor-wait disabled:opacity-60"
              :aria-label="`Remove ${opportunity.name} from favorites`"
              :data-testid="`remove-favorite-${opportunity.id}`"
              :disabled="isRemoving(opportunity.id)"
              @click="removeFavorite(opportunity)"
            >
              <LoaderCircle v-if="isRemoving(opportunity.id)" class="size-4 animate-spin" aria-hidden="true" />
              <Star v-else class="size-4 fill-current" aria-hidden="true" />
            </button>
            <div class="min-w-0">
              <a :href="`/opportunities/${opportunity.id}`" class="block truncate text-sm font-semibold text-land-ink hover:text-land-primary">
                {{ opportunity.name }}
              </a>
              <p class="mt-1 text-xs text-land-ink-soft">{{ opportunity.market }} / {{ opportunity.county }}, {{ opportunity.state }}</p>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <StateBadge :label="opportunity.stageLabel" tone="blue" />
                <StateBadge :label="opportunity.priorityLabel" :tone="priorityTone(opportunity.priority)" />
                <StateBadge :label="opportunity.screeningSummary?.statusLabel || 'Not run'" :tone="opportunity.screeningSummary?.statusTone || 'slate'" />
                <StateBadge :label="`Saved ${formatDate(opportunity.favoritedAt)}`" tone="amber" />
              </div>
            </div>
          </div>

          <div>
            <StateBadge :label="opportunity.health?.stateLabel || 'Unknown'" :tone="opportunity.health?.tone || 'slate'" />
            <p class="mt-2 line-clamp-2 text-xs leading-5 text-land-ink-soft">{{ healthReasons(opportunity).join(' · ') || opportunity.health?.nextAction }}</p>
          </div>

          <div>
            <div class="flex items-center gap-2">
              <ProgressBar class="min-w-36 flex-1" :value="safePercent(opportunity.diligenceProgress)" :tone="progressTone(opportunity)" />
              <span class="text-xs font-semibold text-land-ink-muted">{{ formatNumber(opportunity.diligenceProgress) }}%</span>
            </div>
            <p class="mt-2 text-xs text-land-ink-soft">{{ formatNumber(opportunity.diligenceSummary?.completed) }}/{{ formatNumber(opportunity.diligenceSummary?.total) }} diligence complete</p>
          </div>

          <div class="flex flex-wrap gap-1.5">
            <StateBadge v-if="opportunity.taskSummary?.overdue" :label="`${opportunity.taskSummary.overdue} overdue`" tone="red" />
            <StateBadge v-if="opportunity.taskSummary?.dueSoon" :label="`${opportunity.taskSummary.dueSoon} due soon`" tone="amber" />
            <StateBadge :label="`${formatNumber(opportunity.openTasks)} open`" tone="slate" />
          </div>

          <a :href="`/opportunities/${opportunity.id}`" class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
            Open
            <ArrowRight class="size-4" aria-hidden="true" />
          </a>
        </article>
      </div>
    </section>

    <section v-else class="mt-4 rounded-md border border-dashed border-land-border bg-land-surface px-6 py-14 text-center">
      <div class="mx-auto grid size-12 place-items-center rounded-md bg-amber-50 text-amber-600">
        <Star class="size-5" aria-hidden="true" />
      </div>
      <h2 class="mt-4 text-lg font-semibold tracking-normal">No favorite opportunities yet</h2>
      <p class="mx-auto mt-2 max-w-md text-sm leading-6 text-land-ink-muted">Pinned acquisition sites appear here for focused review.</p>
      <a href="/opportunities" class="mt-5 inline-flex h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover">
        <CircleDollarSign class="size-4" aria-hidden="true" />
        View pipeline
      </a>
    </section>
  </AppShell>
</template>

<script setup>
import { ArrowRight, CircleDollarSign, LoaderCircle, MapPinned, Star } from '@lucide/vue'
import { computed, ref } from 'vue'
import { normalizeErrors, requestJson } from '@/api/client'
import AppShell from '@/components/shared/app_shell.vue'
import ProgressBar from '@/components/shared/progress_bar.vue'
import StateBadge from '@/components/shared/state_badge.vue'

const props = defineProps({
  payload: {
    type: Object,
    required: true
  }
})

const payload = ref(props.payload)
const opportunities = ref([...(props.payload.opportunities || [])])
const notice = ref('')
const errors = ref([])
const removingIds = ref([])

const workspace = computed(() => payload.value.workspace || {})
const favoriteCount = computed(() => opportunities.value.length)
const attentionCount = computed(() => opportunities.value.filter((opportunity) => needsAction(opportunity)).length)
const averageDiligence = computed(() => {
  if (opportunities.value.length === 0) return 0

  const total = opportunities.value.reduce((sum, opportunity) => sum + Number(opportunity.diligenceProgress || 0), 0)

  return Math.round(total / opportunities.value.length)
})

const removeFavorite = async (opportunity) => {
  removingIds.value = [...removingIds.value, opportunity.id]
  errors.value = []

  try {
    const data = await requestJson(`/opportunities/${opportunity.id}/favorite`, {
      method: 'DELETE'
    })

    opportunities.value = opportunities.value.filter((existingOpportunity) => existingOpportunity.id !== opportunity.id)
    notice.value = `${data.opportunity.name} removed from favorites.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    removingIds.value = removingIds.value.filter((id) => id !== opportunity.id)
  }
}

const isRemoving = (id) => removingIds.value.includes(id)
const safePercent = (value) => Math.min(100, Math.max(0, Number(value || 0)))
const healthReasons = (opportunity) => opportunity.health?.reasons || []
const needsAction = (opportunity) => ['attention', 'blocked', 'overdue', 'stale', 'incomplete'].includes(opportunity.health?.state)
const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0))
const formatDate = (value) => {
  if (!value) return 'recently'

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric'
  }).format(new Date(value))
}
const priorityTone = (priority) => ({
  low: 'slate',
  normal: 'blue',
  high: 'amber',
  urgent: 'red'
})[priority] || 'slate'
const progressTone = (opportunity) => {
  if (Number(opportunity.diligenceSummary?.blocked || 0) > 0 || Number(opportunity.diligenceSummary?.failed || 0) > 0) return 'red'
  if (Number(opportunity.diligenceSummary?.overdue || 0) > 0 || Number(opportunity.diligenceSummary?.dueSoon || 0) > 0) return 'amber'
  if (safePercent(opportunity.diligenceProgress) === 100) return 'green'

  return 'blue'
}
const rowStateClasses = (opportunity) => ({
  red: 'border-l-red-500',
  amber: 'border-l-amber-500',
  green: 'border-l-emerald-500',
  blue: 'border-l-blue-500',
  violet: 'border-l-violet-500',
  slate: 'border-l-transparent'
})[opportunity.health?.tone] || 'border-l-transparent'
</script>
